/**
 * Parses the payload block an operator pastes out of a needs-attention Slack
 * message into the §A ingest payload the resolve request is built from.
 *
 * Pure, and returns reason *tokens* rather than display text, the same rule
 * `SalesforceAccounts/lib/salesforceOutcome.ts` sets at its top: the caller
 * maps a token to an i18n key.
 *
 * **Shape only, by design.** The five top-level keys must be present and
 * non-empty and `licenseLines` must hold objects with the five line keys.
 * The email format, the `validFrom` / `validTo` date pattern, the per-line
 * `1..100` quantity bound and the summed 100-licence cap are deliberately
 * *not* re-checked here. The server validates the whole schema synchronously
 * and answers 400 before the 202, so a second copy of those rules in
 * TypeScript would buy a marginally better message and a guaranteed drift the
 * moment the contract moves.
 */
import type {
  SalesforceLicenseLine,
  SalesforceOpportunityIngestRequest,
} from '@/generated/http-clients/v3ApiSchemas'

/** Why a paste could not be turned into a payload. Never display text. */
export type PayloadParseFailure =
  /** Nothing pasted yet, or only whitespace. Not an error the operator made. */
  | 'empty'
  /** Not JSON at all, or JSON that is not an object. */
  | 'malformed'
  /** Parsed, but required fields are missing or empty. See `missing`. */
  | 'incomplete'

export type PayloadParseResult =
  | {
      ok: true
      payload: SalesforceOpportunityIngestRequest
      /** Sum of every line's `quantity`. Display only, never re-validated. */
      licenceTotal: number
    }
  | {
      ok: false
      reason: PayloadParseFailure
      /** Dotted paths of what was missing. Only set when `incomplete`. */
      missing: string[]
    }

/** Top-level keys of `SalesforceOpportunityIngestRequest`. */
const REQUIRED_KEYS = [
  'opportunityId',
  'salesforceAccountId',
  'orgOwner',
  'company',
  'licenseLines',
] as const

/** The top-level keys whose value has to be a non-blank string. */
const TEXT_KEYS = ['opportunityId', 'salesforceAccountId', 'company'] as const

/** Keys of `SalesforceOrgOwner`. */
const OWNER_KEYS = ['email', 'firstName', 'lastName'] as const

/** Keys of `SalesforceLicenseLine`. */
const LINE_KEYS = [
  'quantity',
  'validFrom',
  'validTo',
  'maxConnections',
  'maxThroughput',
] as const

const FENCE = '```'

/**
 * Strips a surrounding triple-backtick fence and trims.
 *
 * An affordance, not a validation rule: the value is copied out of a Slack
 * code block, and how much of the fence comes with it depends on how the
 * operator selected it. Rejecting a paste for carrying its own fence would be
 * pedantry about the transport.
 *
 * The fence is removed by slicing, never by a line-oriented regex. Slack puts
 * both fences on the *same line* whenever the block is one line (a single
 * opportunity, or a minified block), and `/^```[^\n]*\n?/` ate that whole
 * line, so the operator who pasted everything was told to paste the payload
 * block.
 *
 * The language tag is dropped separately, and only up to the JSON's opening
 * brace or bracket, so it cannot eat content on a one-line paste.
 */
export function unfence(raw: string): string {
  const trimmed = raw.trim()
  if (!trimmed.startsWith(FENCE)) {
    return trimmed
  }

  const opened = trimmed.slice(FENCE.length)
  const body = opened.endsWith(FENCE) ? opened.slice(0, -FENCE.length) : opened

  return body.replace(/^[a-zA-Z]*\s*(?=[{[])/, '').trim()
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** Present, a string, and not blank once trimmed. */
function hasText(source: Record<string, unknown>, key: string): boolean {
  const value = source[key]
  return typeof value === 'string' && value.trim().length > 0
}

/** Absent required keys, then the present-but-blank string keys. */
function findMissingTopLevel(candidate: Record<string, unknown>): string[] {
  const absent = REQUIRED_KEYS.filter((key) => !(key in candidate))
  const blank = TEXT_KEYS.filter(
    (key) => key in candidate && !hasText(candidate, key),
  )
  return [...absent, ...blank]
}

/** Nothing when the owner is absent: that is already a top-level miss. */
function findMissingOwner(candidate: Record<string, unknown>): string[] {
  if (!('orgOwner' in candidate)) {
    return []
  }
  const owner = candidate.orgOwner
  if (!isRecord(owner)) {
    return ['orgOwner']
  }
  return OWNER_KEYS.filter((key) => !hasText(owner, key)).map(
    (key) => `orgOwner.${key}`,
  )
}

/** One line's misses, as dotted paths carrying the line's index. */
function findMissingLine(line: unknown, index: number): string[] {
  if (!isRecord(line)) {
    return [`licenseLines[${index}]`]
  }
  return LINE_KEYS.filter(
    (key) => line[key] === undefined || line[key] === null,
  ).map((key) => `licenseLines[${index}].${key}`)
}

/** Nothing when the array is absent: that is already a top-level miss. */
function findMissingLines(candidate: Record<string, unknown>): string[] {
  if (!('licenseLines' in candidate)) {
    return []
  }
  const lines = candidate.licenseLines
  if (!Array.isArray(lines) || lines.length === 0) {
    return ['licenseLines']
  }
  // Arguments named rather than `flatMap(findMissingLine)`: `flatMap` passes
  // `(element, index, array)`, so a bare reference silently binds whatever
  // parameters the callback happens to declare. It is correct today, and a
  // third parameter added to `findMissingLine` later would quietly receive
  // the whole array.
  return lines.flatMap((line, index) => findMissingLine(line, index))
}

/**
 * Collects the dotted paths of everything the payload is missing.
 *
 * Split into one collector per level rather than one loop per level in a
 * single function: the four checks share nothing but the output array, and
 * folding them together made a function nobody could hold in their head (and
 * a Sonar cognitive-complexity smell at 22 against a limit of 15).
 *
 * The order is load-bearing only in that the specs assert it: top level
 * first, then the owner, then the licence lines.
 */
function findMissing(candidate: Record<string, unknown>): string[] {
  return [
    ...findMissingTopLevel(candidate),
    ...findMissingOwner(candidate),
    ...findMissingLines(candidate),
  ]
}

/**
 * Sums `quantity` across the lines for display.
 *
 * A non-numeric quantity contributes nothing rather than turning the total
 * into `NaN`: the shape check has already passed by this point, so the value
 * exists, and whether it is a *valid* number is the server's 400 to make. A
 * `NaN` here would put "NaN licences" in front of the operator instead.
 */
function sumQuantities(lines: SalesforceLicenseLine[]): number {
  return lines.reduce(
    (total, line) =>
      typeof line.quantity === 'number' && Number.isFinite(line.quantity)
        ? total + line.quantity
        : total,
    0,
  )
}

export function parsePayloadBlock(raw: string): PayloadParseResult {
  const source = unfence(raw)

  if (source.length === 0) {
    return { ok: false, reason: 'empty', missing: [] }
  }

  let candidate: unknown
  try {
    candidate = JSON.parse(source)
  } catch {
    return { ok: false, reason: 'malformed', missing: [] }
  }

  if (!isRecord(candidate)) {
    return { ok: false, reason: 'malformed', missing: [] }
  }

  const missing = findMissing(candidate)
  if (missing.length > 0) {
    return { ok: false, reason: 'incomplete', missing }
  }

  // Narrowed to the §A fields, never the whole object.
  //
  // A payload block copied from a needs-attention message that came from a
  // *resolution* rather than from the Salesforce ingest carries the four
  // resolution fields too — `withResolutionFields` puts the operator's own
  // `createNewOrg`, `createNewAccount`, `orgId` and `reason` in it. Passing
  // those through let a stale `orgId` from the paste survive into the request
  // whenever the operator chose to create a new organisation, since the form
  // omits its own `orgId` in that case and so overrides nothing.
  //
  // The contract ignores `orgId` when `createNewOrg` is true, so nothing was
  // mis-delivered, but the request carried a value nobody chose. Picking the
  // fields makes the paste's origin irrelevant to what is sent.
  const fields = candidate as unknown as SalesforceOpportunityIngestRequest
  const payload: SalesforceOpportunityIngestRequest = {
    opportunityId: fields.opportunityId,
    salesforceAccountId: fields.salesforceAccountId,
    orgOwner: fields.orgOwner,
    company: fields.company,
    licenseLines: fields.licenseLines,
  }

  return {
    ok: true,
    payload,
    licenceTotal: sumQuantities(payload.licenseLines),
  }
}

/**
 * The resolution fields a block carries when the case came from the resolve
 * endpoint rather than from the Salesforce ingest.
 *
 * `withResolutionFields` puts the previous operator's own decisions in the
 * needs-attention message, so a re-submission should not make the next reader
 * re-derive them. `parsePayloadBlock` deliberately strips these from the
 * payload it returns, so that a stale value cannot leak into the request; this
 * reads them separately, for the form to *offer* rather than to send.
 *
 * `reason` is deliberately not among them. It is the operator's own
 * justification for the audit log, and pre-filling someone else's prose would
 * record their words as the current operator's.
 */
export type PastedResolutionFields = {
  createNewOrg: boolean
  createNewAccount: boolean
  /** Absent whenever the previous decision was to create a new organisation. */
  orgId: string
}

/**
 * Returns `null` unless the block carries both booleans.
 *
 * Keyed on the two booleans, never on `orgId`: that one is conditional,
 * written only when non-null, and legitimately absent whenever `createNewOrg`
 * is true. A non-boolean in either slot is treated as absent rather than
 * coerced, since a pre-filled radio is an answer the operator will be held to.
 */
export function readResolutionFields(
  raw: string,
): PastedResolutionFields | null {
  let candidate: unknown
  try {
    candidate = JSON.parse(unfence(raw))
  } catch {
    return null
  }
  if (!isRecord(candidate)) {
    return null
  }

  const { createNewOrg, createNewAccount, orgId } = candidate
  if (
    typeof createNewOrg !== 'boolean' ||
    typeof createNewAccount !== 'boolean'
  ) {
    return null
  }

  return {
    createNewOrg,
    createNewAccount,
    orgId: typeof orgId === 'string' ? orgId : '',
  }
}

/**
 * A key for "which case is in the box, and what decisions does it carry".
 *
 * The form applies a pasted block's decisions once per identity. Keying on
 * the not-ok to ok *transition* instead was wrong: selecting the whole
 * textarea and pasting a different valid block, which is exactly what
 * "Replace payload block" is for, never passes through a failed parse, so
 * the previous case's `createNewOrg`, `createNewAccount` and `orgId` stayed
 * standing beside the new company's data, and submitting delivered one
 * opportunity's licences into another opportunity's organisation.
 *
 * The opportunity and the Salesforce account say which case this is; the
 * resolution fields say what the block decided about it. Deliberately *not*
 * the whole payload: the identity is recomputed on every keystroke while the
 * box is open, and including fields the operator may be editing (the company
 * name, a licence line) would re-collapse the box under them mid-edit.
 */
export function resolutionIdentity(
  payload: SalesforceOpportunityIngestRequest,
  fields: PastedResolutionFields | null,
): string {
  const decisions = fields
    ? `${fields.createNewOrg}|${fields.createNewAccount}|${fields.orgId}`
    : 'none'
  return `${payload.opportunityId}|${payload.salesforceAccountId}|${decisions}`
}
